/**
 * @fileoverview What to plant's second read (E19; owner, 29 Oct, "e19 ok"): the See them card and the search's
 * catalogue, asked once the list is in and its plan is open, so her own list and to-do rows never wait for the whole
 * catalogue. The server decides both (services/plants/whatToPlantCatalogue).
 *
 * Where: WhatToPlantPage.
 * API: GET /plants/what-to-plant/catalogue?lang= (api/whatToPlant).
 * Caching: keyed like the list (the garden, its own day, the language); an add marks it stale without reading it again
 * on the page (useAddCrop).
 *
 * @module useWhatToPlantCatalogue
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/shared/hooks/useAuth';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { getWhatToPlantCatalogue, type WhatToPlantCatalogue } from '@/shared/api/whatToPlant';
import { chosenGarden } from '@/shared/api/gardenMembers';
import { QUERY_STALE_TIME_MS } from '@/config/queryClient';

/** The first word of the second read's React Query key. @kept K1 */
export const WHAT_TO_PLANT_CATALOGUE_QUERY_KEY = 'what-to-plant-catalogue';

/** What the page is handed. */
export interface WhatToPlantCatalogueReading {
  /** The server's answer, or undefined until it is in. */
  data: WhatToPlantCatalogue | undefined;
  /** True while it is on its way (the page's calm placeholder). */
  isLoading: boolean;
  /** The failure, or null. */
  error: unknown;
}

/**
 * Reads the See them card and the search's catalogue, once asked to.
 *
 * @param open - The list's plan is open (the server said access 'open'); nothing is read before.
 * @returns The answer, whether it is on its way, and any failure.
 */
export function useWhatToPlantCatalogue(open: boolean): WhatToPlantCatalogueReading {
  const { mysqlUser } = useAuth();
  const { i18n: { language } } = useTranslation();
  const { date, known } = useGardenPostcode();
  const gardenId = chosenGarden() ?? mysqlUser?.user_id ?? null;
  const { data, isLoading, error } = useQuery({
    queryKey: [WHAT_TO_PLANT_CATALOGUE_QUERY_KEY, gardenId, date, language],
    queryFn: () => getWhatToPlantCatalogue(language),
    enabled: Boolean(mysqlUser) && known && open,
    staleTime: QUERY_STALE_TIME_MS,
  });
  return { data, isLoading: open && (isLoading || !known), error: error ?? null };
}
